import { getCurrentUser, setCurrentUser, setToken } from "@/utils";

const API_BASE = process.env.REACT_APP_API_BASE;

const headers = {
  headers: {
    "Content-Type": "application/json",
  },
};

// Redeem an invite code (no Stripe / no credit card) when the visitor arrived
// via ?invite=<code>. The backend resolves the invite to its artist and enforces
// the redemption cap, validity, and idempotency, so this is safe to call on
// every login and for any invite. Returns the parsed response so callers can
// tell whether the invite was actually granted.
const inviteSubscribe = async (invite) => {
  try {
    const user = getCurrentUser();
    const response = await fetch(`${API_BASE}/subscribe/invite`, {
      ...headers,
      method: "POST",
      body: JSON.stringify({
        user_key: user?.key,
        invite,
        country: localStorage.getItem("country"),
      }),
    });
    return await response.json().catch(() => ({}));
  } catch (error) {
    console.error("Error redeeming invite code:", error);
    return {};
  }
};

// Redeem an invite already stashed in localStorage for an authenticated user
// (e.g. an existing user who clicked the link while logged in and never went
// through verify/createUser). Only clears the invite and refreshes when it was
// granted, so a cap-full/invalid attempt doesn't silently drop the stored invite.
export const redeemStoredInvite = async () => {
  const invite = localStorage.getItem("invite");
  if (!invite) return;

  const result = await inviteSubscribe(invite);
  if (result?.granted) {
    localStorage.removeItem("invite");
    // drop the ?invite= param and reload so subscription state refreshes
    window.location.href = window.location.pathname;
  }
};

const handleRedirect = async () => {
  const invite = localStorage.getItem("invite");
  if (invite) {
    const result = await inviteSubscribe(invite);
    if (result?.granted) {
      localStorage.removeItem("invite");
    }
  }

  const redirect = localStorage.getItem("redirect");
  if (redirect) {
    window.location.href = redirect;
  } else {
    window.location.href = "/";
  }
};

const REQUEST_TIMEOUT_MS = 30000;

export const sendVerification = async (phone) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const endpoint = new URL(API_BASE + "/send_verification");
    endpoint.searchParams.append("phone", phone);
    const response = await fetch(endpoint.toString(), {
      ...headers,
      signal: controller.signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        ok: false,
        error: data.error || "Couldn't send a code. Please try again.",
      };
    }
    return { ok: true, ...data };
  } catch (error) {
    console.log(error);
    const message =
      error.name === "AbortError"
        ? "The request timed out. Please try again."
        : "Couldn't send a code. Please check your connection and try again.";
    return { ok: false, error: message };
  } finally {
    clearTimeout(timeout);
  }
};

export const verify = async (phone, code) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const endpoint = new URL(API_BASE + "/verify");
    endpoint.searchParams.append("phone", phone);
    endpoint.searchParams.append("code", code);
    const response = await fetch(endpoint.toString(), {
      ...headers,
      signal: controller.signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { ok: false, error: data.error || "Invalid code. Please try again." };
    }
    if (data.user) {
      setCurrentUser(data.user);
      setToken(data.token);
      await handleRedirect();
    }
    return data;
  } catch (error) {
    console.log(error);
    const message =
      error.name === "AbortError"
        ? "The request timed out. Please try again."
        : "Invalid code. Please try again.";
    return { ok: false, error: message };
  } finally {
    clearTimeout(timeout);
  }
};

export const createUser = async (phone, name, terms, profile) => {
  try {
    const formData = new FormData();
    formData.append("phone", phone);
    formData.append("name", name);
    formData.append("terms", terms);
    if (profile) {
      formData.append("profile", profile);
    }

    const response = await fetch(`${API_BASE}/user/create`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create user");
    }

    const data = await response.json();
    setCurrentUser(data.user);
    setToken(data.token);
    if (terms) {
      localStorage.setItem("terms_update_2026_dismissed", "true");
    }
    await handleRedirect();
    return data;
  } catch (error) {
    console.error("Error creating user:", error);
    alert(
      error.message ||
        "Something went wrong. Please contact support@notebook.fm",
    );
    throw error;
  }
};
